import { api } from './client';
import { downloadBlob } from './iaaExports';

/** View model's Excel export: the columns shown (in order, with their width; `pct` = a model's %
 *  column), the frozen count, and one cell per column per row (a % as a number string; null = "-"). */
export interface ModelViewXlsxCol { label: string; pct: boolean; width: number }
export interface ModelViewXlsxRow { kind: 'group' | 'line' | 'total'; depth: number; cells: (string | null)[] }
export interface ModelViewXlsxIn {
  view: string | null; grouping: string | null; models: string[];
  columns: ModelViewXlsxCol[]; freeze: number; rows: ModelViewXlsxRow[];
}

/** Model Portfolios (M1: buckets, migration 0123; C-302). Read by `portfolios.read`; changed by
 *  `model.manage` (Admin by default, granted per person). Weights travel as strings (exact).
 *  Design: decisions/2026-07-25_model-portfolios-design.md; Pedro's M1 answers in OPEN_ITEMS
 *  "M1-PLAN". */

export type BucketMemberKind = 'security' | 'cash';

export interface BucketMemberIn {
  kind: BucketMemberKind;
  security_id?: string | null;
  cash_currency?: string | null;
  /** % of the bucket as typed, at most 2 decimals. */
  weight: string;
}

export interface BucketMember {
  kind: BucketMemberKind;
  security_id: string | null;
  isin: string | null;
  currency: string | null;
  /** The security's name, or "Cash EUR". */
  name: string | null;
  cash_currency: string | null;
  /** null for cash. */
  approved: boolean | null;
  weight_entered: string;
  /** The resolved weight the app keeps (adds up to exactly 100 across the bucket). */
  weight_pct: string;
  /** Why this line shows red (no longer Approved, or no longer in the master); null if fine. */
  flag: string | null;
}

export interface Bucket {
  id: string;
  name: string;
  description: string | null;
  members: BucketMember[];
  member_count: number;
  security_count: number;
  cash_count: number;
  /** Names of the members that show red. */
  flagged: string[];
  /** M2: the models built with this bucket (it cannot be deleted while any). */
  used_in_models: { id: string; name: string }[];
  updated_at: string | null;
}

/** M2 (migration 0124): a model = buckets weighted as % of the whole model. */
export interface ModelComponentRow {
  /** M-SEC (migration 0125): a bucket, or a security held directly. */
  kind: 'bucket' | 'security';
  component_id: string;
  name: string | null;
  isin?: string | null;
  currency?: string | null;
  weight_entered: string;
  weight_pct: string;
  /** null for a security held directly. */
  member_count: number | null;
  flagged: string[];
  /** Why this line shows red (a member of the bucket no longer Approved); null if fine. */
  flag: string | null;
}

export interface ModelPortfolio {
  id: string;
  name: string;
  description: string | null;
  components: ModelComponentRow[];
  component_count: number;
  /** Names of the buckets that show red. */
  flagged: string[];
  updated_at: string | null;
  /** TRADE-CONS 1b: the consolidations / portfolios that follow it (Delete is greyed while any do). */
  followed_by?: string[];
}

/** M3: a model's effective weight per security / cash line (bucket % × member %), full precision. */
export interface ModelWeightRow {
  kind: 'security' | 'cash';
  security_id: string | null;
  isin: string | null;
  currency: string | null;
  name: string | null;
  approved: boolean | null;
  weight_pct: string;
  /** bucket_id null = held directly in the model (M-SEC). */
  sources: { bucket_id: string | null; bucket: string | null; bucket_pct: string; member_pct: string }[];
  flag: string | null;
}

export interface ModelWeights {
  id: string;
  name: string;
  rows: ModelWeightRow[];
  total_pct: string;
  security_count: number;
  cash_count: number;
}

/** View model (Pedro 2026-11-01): one row per security / cash currency across the chosen models,
 *  classified as View portfolio classifies a holding; `by_model` = % of each model (absent = not in it). */
export interface ModelViewRow {
  key: string; is_cash: boolean; name: string | null; isin: string | null; currency: string | null;
  bb_ticker: string | null; figi: string | null;
  security_id: string | null; approved: boolean | null; flag: string | null;
  classifications: Record<string, string[]>; orders: Record<string, number[]>; non_classified: boolean;
  by_model: Record<string, string>;
}
export interface ModelView {
  view: { id: string; name: string } | null;
  lenses: { code: string; name: string; tiers: { level: number; name: string }[] }[];
  models: { id: string; name: string; total_pct: string }[];
  rows: ModelViewRow[];
}

export interface ModelIn {
  name: string;
  description?: string | null;
  components: { kind: 'bucket' | 'security'; component_id: string; weight: string }[];
}

export interface BucketIn {
  name: string;
  description?: string | null;
  members: BucketMemberIn[];
}

export const modelsApi = {
  buckets: () => api.get<{ items: Bucket[]; total: number; tolerance: string }>('/models/buckets'),
  bucket: (id: string) => api.get<Bucket>(`/models/buckets/${encodeURIComponent(id)}`),
  createBucket: (body: BucketIn) => api.post<Bucket>('/models/buckets', body),
  updateBucket: (id: string, body: BucketIn) => api.put<Bucket>(`/models/buckets/${encodeURIComponent(id)}`, body),
  deleteBucket: (id: string) => api.delete<void>(`/models/buckets/${encodeURIComponent(id)}`),
  models: () => api.get<{ items: ModelPortfolio[]; total: number; tolerance: string }>('/models'),
  model: (id: string) => api.get<ModelPortfolio>(`/models/${encodeURIComponent(id)}`),
  createModel: (body: ModelIn) => api.post<ModelPortfolio>('/models', body),
  updateModel: (id: string, body: ModelIn) => api.put<ModelPortfolio>(`/models/${encodeURIComponent(id)}`, body),
  deleteModel: (id: string) => api.delete<void>(`/models/${encodeURIComponent(id)}`),
  weights: (id: string) => api.get<ModelWeights>(`/models/${encodeURIComponent(id)}/weights`),
  /** View model: the chosen models side by side on a classification view (`portfolios.read`). */
  view: (ids: string[], schemeId: string | null) => {
    const q = new URLSearchParams();
    for (const id of ids) q.append('ids', id);
    if (schemeId) q.set('scheme_id', schemeId);
    return api.get<ModelView>(`/models/view?${q.toString()}`);
  },
  /** View model → Export to Excel (Pedro 2026-11-02): the table as shown, written by the server with real %. */
  downloadView: async (body: ModelViewXlsxIn): Promise<void> => {
    const res = await fetch('/models/view.xlsx', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('The Excel file could not be generated.');
    downloadBlob(await res.blob(), 'view-model.xlsx');
  },
  /** M3: the effective weights as Excel (one model's composition; `portfolios.read`). */
  downloadWeights: async (id: string, name: string): Promise<void> => {
    const res = await fetch(`/models/${encodeURIComponent(id)}/weights.xlsx`, { credentials: 'include' });
    if (!res.ok) throw new Error('The weights could not be generated.');
    const safe = name.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'model';
    downloadBlob(await res.blob(), `model-${safe}-weights.xlsx`);
  },
};

/** Where securities are held (MQ5 / MF2): the buckets and the portfolios (latest positions)
 *  holding each one. `securities.manage` (MF3). */
export interface SecurityUsage {
  security_id: string;
  isin: string | null;
  currency: string | null;
  name: string | null;
  buckets: { id: string; name: string }[];
  /** M-SEC: models holding the security directly. */
  models: { id: string; name: string }[];
  portfolios: { ref: string; label: string; as_of: string }[];
}

export const securityUsageApi = {
  of: (ids: string[]) => api.post<{ items: SecurityUsage[]; total: number }>('/securities/usage', { ids }),
  /** The downloadable list (.xlsx); one audit row per download. */
  download: async (ids: string[]): Promise<void> => {
    const res = await fetch('/securities/usage/export', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) throw new Error('The list could not be generated.');
    downloadBlob(await res.blob(), 'where-securities-are-held.xlsx');
  },
};
